import React from 'react';
import { motion, useInView } from 'framer-motion';
import { useRef } from 'react';
import { CheckCircle2 } from 'lucide-react';

const stats = [
  { number: '100+', label: 'Projects Delivered' },
  { number: 'Happy', label: 'Customers' },
  { number: '98%', label: 'Quality Score' },
  { number: '24/7', label: 'Support Available' },
];

const whyChooseUs = [
  'Experienced QA Engineers',
  'Manual & Automated Testing',
  'Faster Release Cycles',
  'Reduced Development Costs',
  'Flexible Engagement Models',
  'Agile & DevOps Ready',
  'Detailed Reporting & Metrics',
  'Global Delivery Support'
];

export const About = () => {
  const ref = useRef(null);
  const isInView = useInView(ref, { once: true, margin: '-100px' });

  return (
    <section ref={ref} className="py-32 bg-[#0f0f10] relative overflow-hidden">
      {/* Background Gradient */}
      <div className="absolute top-0 right-0 w-1/2 h-full bg-gradient-to-br from-[#8B1538]/10 to-transparent" />
      
      <div className="max-w-7xl mx-auto px-6 relative z-10">
        {/* Section Number */}
        <motion.div
          initial={{ opacity: 0, x: -50 }}
          animate={isInView ? { opacity: 1, x: 0 } : {}}
          transition={{ duration: 0.8, ease: [0.22, 1, 0.36, 1] }}
          className="text-[#D4AF37]/20 text-8xl md:text-9xl font-bold mb-8"
        >
          01
        </motion.div>

        <div className="grid lg:grid-cols-2 gap-16 items-start">
          <div>
            <motion.h2
              initial={{ opacity: 0, y: 30 }}
              animate={isInView ? { opacity: 1, y: 0 } : {}}
              transition={{ duration: 0.8, delay: 0.2, ease: [0.22, 1, 0.36, 1] }}
              className="text-4xl md:text-5xl text-white mb-8 leading-tight"
              style={{ fontFamily: "'Playfair Display', serif", fontWeight: 500 }}
            >
              About <span className="italic text-[#D4AF37]">INDOAXIUM</span>
            </motion.h2>
            
            {/* Decorative divider */}
            <motion.div
              initial={{ scaleX: 0 }}
              animate={isInView ? { scaleX: 1 } : {}}
              transition={{ duration: 0.8, delay: 0.3, ease: [0.22, 1, 0.36, 1] }}
              className="h-px w-16 bg-gradient-to-r from-[#D4AF37] to-transparent mb-8 origin-left"
            />
            
            <motion.p
              initial={{ opacity: 0, y: 30 }}
              animate={isInView ? { opacity: 1, y: 0 } : {}}
              transition={{ duration: 0.8, delay: 0.4, ease: [0.22, 1, 0.36, 1] }}
              className="text-xl md:text-2xl text-gray-200 leading-[1.7] mb-6 font-light"
              style={{ fontFamily: "'Cormorant Garamond', serif" }}
            >
              At <span className="italic text-[#D4AF37]">INDOAXIUM Software Services</span>, we specialize in delivering 
              end-to-end software testing services that improve product quality, accelerate releases, 
              and reduce business risks.
            </motion.p>

            <motion.p
              initial={{ opacity: 0, y: 30 }}
              animate={isInView ? { opacity: 1, y: 0 } : {}}
              transition={{ duration: 0.8, delay: 0.5, ease: [0.22, 1, 0.36, 1] }}
              className="text-xl md:text-2xl text-gray-300 leading-[1.7] mb-12 font-light"
              style={{ fontFamily: "'Cormorant Garamond', serif" }}
            >
              Our experienced QA professionals combine industry best practices, automation frameworks, 
              and modern testing methodologies to identify defects early and ensure 
              <span className="italic text-[#D4AF37]"> exceptional user experiences</span>.
            </motion.p>

            {/* Stats */}
            <div className="grid grid-cols-2 gap-6">
              {stats.map((stat, index) => (
                <motion.div
                  key={index}
                  initial={{ opacity: 0, scale: 0.8 }}
                  animate={isInView ? { opacity: 1, scale: 1 } : {}}
                  transition={{ duration: 0.5, delay: 0.5 + index * 0.1 }}
                  className="bg-gradient-to-br from-[#1a1a1a] to-[#2d1f1f] p-6 rounded-sm border border-[#D4AF37]/20"
                >
                  <div className="text-3xl md:text-4xl font-bold text-[#D4AF37] mb-2">{stat.number}</div>
                  <div className="text-sm text-gray-400">{stat.label}</div>
                </motion.div>
              ))}
            </div>
          </div>

          {/* Why Choose Us */}
          <motion.div
            initial={{ opacity: 0, x: 50 }}
            animate={isInView ? { opacity: 1, x: 0 } : {}}
            transition={{ duration: 0.8, delay: 0.4, ease: [0.22, 1, 0.36, 1] }}
            className="bg-gradient-to-br from-[#1a1a1a] to-[#2d1f1f] p-6 md:p-8 rounded-sm border border-[#D4AF37]/20 self-start"
          >
            <h3 className="text-xl md:text-2xl font-bold text-white mb-6">
              Why Choose <span className="text-[#D4AF37]">Us</span>
            </h3>
            
            <div className="grid grid-cols-2 gap-x-4 gap-y-3">
              {whyChooseUs.map((item, index) => (
                <motion.div
                  key={index}
                  initial={{ opacity: 0, x: -20 }}
                  animate={isInView ? { opacity: 1, x: 0 } : {}}
                  transition={{ duration: 0.5, delay: 0.6 + index * 0.05 }}
                  className="flex items-center gap-2 group"
                >
                  <CheckCircle2 className="w-4 h-4 text-[#D4AF37] flex-shrink-0 group-hover:scale-110 transition-transform duration-300" />
                  <span className="text-xs md:text-sm text-gray-300 group-hover:text-white transition-colors duration-300">{item}</span>
                </motion.div>
              ))}
            </div>
          </motion.div>
        </div>
      </div>
    </section>
  );
};